import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BellRing, MapPin, PartyPopper, Loader2, Check, ArrowDownCircle, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { isStandalone, isIOS } from "@/lib/install";
import {
  pushSupport,
  subscribeCurrent,
  setDecision as setPushDecision,
  isPreDenied,
} from "@/lib/push";
import {
  locationSupport,
  getCurrentPositionOnce,
  setLocationDecision,
  permissionState as getGeoPermState,
} from "@/lib/location";

/**
 * PwaWelcomeModal
 *
 * Appears ONLY inside the installed PWA (standalone mode). Because iOS
 * PWAs run in a separate permission sandbox from Safari, guests who
 * previously said "yes" in the browser must reconfirm inside the app.
 * This modal chains Notification + Geolocation behind a single tap so
 * they never need to hunt through the menu.
 *
 * Decision key: `em_pwa_welcome_state`
 *   - `null` / `'later'` → show on next launch
 *   - `'done'` → accepted at least one permission (don't show again)
 *   - `'never'` → guest tapped "Not for me" (don't show again)
 */

const STATE_KEY = "em_pwa_welcome_state";

const COPY = {
  en: {
    kicker: "Welcome to the app",
    title: "One tap, and we\u2019re set",
    body: "Turn on notifications and location so Andreas can send you gentle reminders \u2014 the countdown, RSVP, ceremony start \u2014 and welcome you the moment you land in Cyprus.",
    highlight: "Both permissions turn on with one tap. You can switch them off any time in your device settings.",
    itemNotifTitle: "Push notifications",
    itemNotifBody: "Countdown, ceremony start, meal choices, dance floor call.",
    itemLocTitle: "Location",
    itemLocBody: "Only while the app is open. A warm welcome when you arrive.",
    allow: "Turn everything on",
    later: "Later",
    never: "Not for me",
    working: "Turning on\u2026",
    doneToast: "All set, koumbare\u2764",
    partialToast: "Almost there \u2014 you can enable the rest any time.",
    iosBounceTitle: "iOS may have hidden the prompt",
    iosBounceBody: "Tap the button again \u2014 iOS sometimes swallows the first request when the app just opened.",
    deniedToast: "That\u2019s okay \u2014 you can enable them later from your device Settings.",
  },
  fr: {
    kicker: "Bienvenue dans l\u2019app",
    title: "Un tap, et c\u2019est bon",
    body: "Active les notifications et la position pour qu\u2019Andreas puisse te rappeler gentiment le compte à rebours, le RSVP, le début de la cérémonie \u2014 et t\u2019accueillir dès ton arrivée à Chypre.",
    highlight: "Les deux autorisations s\u2019activent d\u2019un seul tap. Désactivables à tout moment dans les réglages de l\u2019appareil.",
    itemNotifTitle: "Notifications",
    itemNotifBody: "Compte à rebours, cérémonie, choix des plats, piste de danse.",
    itemLocTitle: "Position",
    itemLocBody: "Uniquement quand l\u2019app est ouverte. Un accueil chaleureux à ton arrivée.",
    allow: "Tout activer",
    later: "Plus tard",
    never: "Pas pour moi",
    working: "Activation\u2026",
    doneToast: "Tout est prêt, koumbare\u2764",
    partialToast: "Presque bon \u2014 tu peux activer le reste plus tard.",
    iosBounceTitle: "iOS a peut-être masqué la demande",
    iosBounceBody: "Retouche le bouton \u2014 iOS avale parfois la première demande à l\u2019ouverture.",
    deniedToast: "Pas de souci \u2014 tu peux les activer plus tard depuis les Réglages de l\u2019appareil.",
  },
  el: {
    kicker: "Καλωσόρισες στην εφαρμογή",
    title: "Ένα tap και είμαστε έτοιμοι",
    body: "Ενεργοποίησε ειδοποιήσεις και τοποθεσία για να σου στέλνει ο Andreas ήπιες υπενθυμίσεις \u2014 αντίστροφη μέτρηση, RSVP, έναρξη τελετής \u2014 και να σε καλωσορίσει μόλις πατήσεις Κύπρο.",
    highlight: "Και οι δύο άδειες ενεργοποιούνται με ένα tap. Απενεργοποιείς όποτε θες από τις ρυθμίσεις της συσκευής.",
    itemNotifTitle: "Ειδοποιήσεις",
    itemNotifBody: "Αντίστροφη μέτρηση, τελετή, επιλογές φαγητού, πίστα.",
    itemLocTitle: "Τοποθεσία",
    itemLocBody: "Μόνο όταν η εφαρμογή είναι ανοιχτή. Ζεστό καλωσόρισμα στην άφιξη.",
    allow: "Ενεργοποίηση όλων",
    later: "Αργότερα",
    never: "Όχι για μένα",
    working: "Ενεργοποίηση\u2026",
    doneToast: "Έτοιμα, κουμπάρε\u2764",
    partialToast: "Σχεδόν εκεί \u2014 μπορείς να ενεργοποιήσεις τα υπόλοιπα αργότερα.",
    iosBounceTitle: "Το iOS ίσως έκρυψε την ερώτηση",
    iosBounceBody: "Πάτησε το κουμπί ξανά \u2014 το iOS μερικές φορές καταπίνει το πρώτο αίτημα με το που ανοίγει η εφαρμογή.",
    deniedToast: "Δεν πειράζει \u2014 μπορείς να τα ενεργοποιήσεις αργότερα από τις Ρυθμίσεις.",
  },
  pl: {
    kicker: "Witaj w aplikacji",
    title: "Jedno dotknięcie i gotowe",
    body: "Włącz powiadomienia i lokalizację, żeby Andreas mógł delikatnie ci przypomnieć \u2014 odliczanie, RSVP, początek ceremonii \u2014 i powitać, gdy tylko wylądujesz na Cyprze.",
    highlight: "Obie zgody włączają się jednym dotknięciem. Wyłączysz je w każdej chwili w ustawieniach urządzenia.",
    itemNotifTitle: "Powiadomienia",
    itemNotifBody: "Odliczanie, ceremonia, wybór posiłków, parkiet.",
    itemLocTitle: "Lokalizacja",
    itemLocBody: "Tylko przy otwartej aplikacji. Ciepłe powitanie po przyjeździe.",
    allow: "Włącz wszystko",
    later: "Później",
    never: "Nie dla mnie",
    working: "Włączanie\u2026",
    doneToast: "Gotowe, kumie\u2764",
    partialToast: "Prawie gotowe \u2014 resztę włączysz później.",
    iosBounceTitle: "iOS mógł ukryć zapytanie",
    iosBounceBody: "Kliknij ponownie \u2014 iOS czasem połyka pierwsze zapytanie tuż po otwarciu aplikacji.",
    deniedToast: "Nic się nie stało \u2014 włączysz je później w Ustawieniach urządzenia.",
  },
};

const PREVIEWS = {
  en: [
    { title: "Ammos Kalambouri weather", body: "Sunny, 27°C. Andreas is horizontal on a lounger." },
    { title: "90 days to Cyprus, koumbare", body: "Time to look at flights. Larnaca airport." },
    { title: "Cats own the taverna", body: "Every taverna has six cats. This is Cyprus." },
  ],
  fr: [
    { title: "Météo Ammos Kalambouri", body: "Ensoleillé, 27°C. Andreas est sur un transat." },
    { title: "90 jours avant Chypre", body: "Regarde les vols. Aéroport de Larnaca." },
    { title: "Les chats du taverna", body: "Six chats par taverne. C'est Chypre." },
  ],
  el: [
    { title: "Καιρός στον Άμμο Καλαμπούρη", body: "Λιακάδα, 27°C. Ο Andreas ξαπλωμένος." },
    { title: "90 μέρες μέχρι Κύπρο", body: "Ώρα να δεις πτήσεις. Αεροδρόμιο Λάρνακας." },
    { title: "Οι γάτες αφεντικά", body: "Κάθε ταβέρνα, έξι γάτες. Έτσι είναι η Κύπρος." },
  ],
  pl: [
    { title: "Pogoda Ammos Kalambouri", body: "Słońce, 27°C. Andreas w poziomie." },
    { title: "90 dni do Cypru, kumie", body: "Czas na loty. Lotnisko Larnaca." },
    { title: "Koty rządzą tawerną", body: "Sześć kotów w każdej. Taki Cypr." },
  ],
};

const readState = () => {  try { return localStorage.getItem(STATE_KEY); } catch { return null; }
};
const writeState = (v) => {
  try { localStorage.setItem(STATE_KEY, v); } catch { /* ignore */ }
};

const PwaWelcomeModal = () => {
  const { lang } = useLang();
  const { isAuthed, authReady } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showIOSBounce, setShowIOSBounce] = useState(false);
  const [pushDone, setPushDone] = useState(false);
  const [locDone, setLocDone] = useState(false);
  const openedOnceRef = useRef(false);

  const c = useMemo(() => COPY[lang] || COPY.en, [lang]);

  const forceOpen = useCallback(async () => {
    // Any component can call `window.dispatchEvent(new Event('em-open-welcome'))`
    // to re-trigger this modal. We clear the state so the guest can go through
    // again even if they previously accepted or dismissed.
    try { localStorage.removeItem(STATE_KEY); } catch { /* ignore */ }
    openedOnceRef.current = true;
    setShowIOSBounce(false);
    // Refresh permission snapshot so the checkmarks are accurate
    try {
      const notifPerm = pushSupport() ? Notification.permission : "unsupported";
      const locPerm = locationSupport() ? await getGeoPermState() : "unsupported";
      setPushDone(notifPerm === "granted");
      setLocDone(locPerm === "granted");
    } catch { /* ignore */ }
    setOpen(true);
  }, []);

  useEffect(() => {
    const handler = () => { forceOpen(); };
    window.addEventListener("em-open-welcome", handler);
    return () => window.removeEventListener("em-open-welcome", handler);
  }, [forceOpen]);

  /**
   * Decide whether to open. Runs when auth becomes ready.
   * Rules:
   *  - Only inside the installed PWA (standalone mode).
   *  - Skip if state is 'never' (guest explicitly said no).
   *  - Skip if state is 'done' AND both permissions are still granted.
   *    If state is 'done' but a perm has since been revoked, re-open so the
   *    guest can grant it again without hunting through settings.
   *  - Skip if both permissions are already `granted` and set state='done'.
   */
  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (openedOnceRef.current) return;
    if (typeof window === "undefined") return;
    if (!isStandalone()) return;

    const state = readState();
    if (state === "never") return;

    (async () => {
      // Detect current permission state
      const notifSupported = pushSupport();
      const locSupported = locationSupport();
      const notifPerm = notifSupported ? Notification.permission : "unsupported";
      const locPerm = locSupported ? await getGeoPermState() : "unsupported";

      const notifOK = notifPerm === "granted" || notifPerm === "unsupported";
      const locOK = locPerm === "granted" || locPerm === "unsupported";

      // Everything already granted (Android usually inherits from browser)
      if (notifOK && locOK) {
        writeState("done");
        return;
      }

      // state='done' but at least one perm is missing → treat as if not done.
      // (Guest may have revoked notifications from device settings.)
      if (state === "done") {
        try { localStorage.removeItem(STATE_KEY); } catch { /* ignore */ }
      }

      // Small delay so the PWA can render its shell first
      const t = setTimeout(() => {
        openedOnceRef.current = true;
        setPushDone(notifPerm === "granted");
        setLocDone(locPerm === "granted");
        setOpen(true);
      }, 900);
      return () => clearTimeout(t);
    })();
  }, [authReady, isAuthed]);

  /**
   * Request notification permission + subscribe.
   * MUST be invoked directly from a user gesture (button click) for iOS.
   */
  const requestNotifications = useCallback(async () => {
    if (!pushSupport()) return { ok: true, skipped: true };
    if (Notification.permission === "granted") {
      // Already granted in this context → just make sure we have a live sub
      try { await subscribeCurrent(lang); } catch { /* ignore */ }
      return { ok: true };
    }
    if (isPreDenied()) {
      return { ok: false, reason: "denied-preexisting" };
    }
    try {
      const res = await subscribeCurrent(lang);
      if (res.ok) return { ok: true };
      if (res.permission === "denied") {
        setPushDecision("denied");
        return { ok: false, reason: "denied" };
      }
      // 'default' — iOS may have hidden the prompt or the guest dismissed it
      return { ok: false, reason: "default" };
    } catch (e) {
      if (e && e.kind === "ios-install-required") return { ok: false, reason: "ios-install-required" };
      return { ok: false, reason: "error", error: e };
    }
  }, [lang]);

  /**
   * Request geolocation permission by asking for one position.
   */
  const requestLocation = useCallback(async () => {
    if (!locationSupport()) return { ok: true, skipped: true };
    const current = await getGeoPermState();
    if (current === "granted") {
      setLocationDecision("granted");
      return { ok: true };
    }
    try {
      const pos = await getCurrentPositionOnce({ timeout: 12_000 });
      if (pos && pos.coords) {
        setLocationDecision("granted");
        return { ok: true };
      }
      return { ok: false, reason: "no-coords" };
    } catch (err) {
      if (err && err.code === 1) {
        setLocationDecision("denied");
        return { ok: false, reason: "denied" };
      }
      return { ok: false, reason: "error" };
    }
  }, []);

  const onAllowEverything = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setShowIOSBounce(false);

    // 1) Notifications first (must be from user gesture)
    const notif = await requestNotifications();
    setPushDone(notif.ok);

    // iOS quirk: if notif came back as 'default', OS swallowed the prompt.
    // Show a hint arrow and stop — guest can tap again.
    if (!notif.ok && notif.reason === "default" && isIOS()) {
      setShowIOSBounce(true);
      setBusy(false);
      return;
    }

    // 2) Location (own OS prompt)
    const loc = await requestLocation();
    setLocDone(loc.ok);

    // 3) Wrap up
    if (notif.ok && loc.ok) {
      toast.success(c.doneToast);
      writeState("done");
      setOpen(false);
    } else if (notif.ok || loc.ok) {
      toast.message(c.partialToast);
      writeState("done"); // At least one accepted — done
      setOpen(false);
    } else {
      // Both failed — treat like Later so we ask again next launch
      toast.message(c.deniedToast);
      writeState("later");
      setOpen(false);
    }
    setBusy(false);
  }, [busy, c, requestLocation, requestNotifications]);

  const onLater = useCallback(() => {
    writeState("later");
    setOpen(false);
  }, []);

  const onNever = useCallback(() => {
    writeState("never");
    setPushDecision("later"); // don't force denied — leave a door open in menu
    setOpen(false);
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[310] flex items-end sm:items-center justify-center bg-foreground/50 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="pwa-welcome-modal"
    >
      <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in-0 slide-in-from-bottom-8 duration-300">
        <button
          type="button"
          onClick={onLater}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5"
          aria-label="Close"
          data-testid="pwa-welcome-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-7">
          <div className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-4">
            <PartyPopper className="w-5 h-5" />
          </div>
          <p className="editorial-kicker text-foreground/50" data-testid="pwa-welcome-kicker">{c.kicker}</p>
          <h2 className="font-serif-display text-2xl sm:text-[28px] leading-tight mt-2" data-testid="pwa-welcome-title">{c.title}</h2>
          <p className="text-sm sm:text-[15px] leading-relaxed text-foreground/75 mt-3" data-testid="pwa-welcome-body">{c.body}</p>

          {/* Permission preview list */}
          <ul className="mt-5 space-y-3">
            <li className="flex items-start gap-3">
              <div className={
                "w-8 h-8 rounded-full shrink-0 flex items-center justify-center transition-colors " +
                (pushDone ? "bg-emerald-500/15 text-emerald-600" : "bg-accent/15 text-accent")
              }>
                {pushDone ? <Check className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{c.itemNotifTitle}</p>
                <p className="text-[12px] text-foreground/60 leading-snug">{c.itemNotifBody}</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className={
                "w-8 h-8 rounded-full shrink-0 flex items-center justify-center transition-colors " +
                (locDone ? "bg-emerald-500/15 text-emerald-600" : "bg-accent/15 text-accent")
              }>
                {locDone ? <Check className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{c.itemLocTitle}</p>
                <p className="text-[12px] text-foreground/60 leading-snug">{c.itemLocBody}</p>
              </div>
            </li>
          </ul>

          <p className="text-[12px] italic text-foreground/50 mt-4">{c.highlight}</p>

          {/* Push previews — show exactly what guests will receive */}
          <div className="mt-4 space-y-2" data-testid="pwa-welcome-previews">
            {(PREVIEWS[lang] || PREVIEWS.en).map((p, i) => (
              <div
                key={i}
                className="rounded-lg border border-border/70 bg-foreground/[0.03] px-3 py-2 flex items-start gap-2.5"
              >
                <div className="w-7 h-7 shrink-0 rounded-md bg-accent/15 text-accent inline-flex items-center justify-center">
                  <BellRing className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-foreground truncate">{p.title}</p>
                  <p className="text-[11px] text-foreground/60 leading-snug truncate">{p.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* iOS "prompt swallowed" hint */}
          {showIOSBounce && (
            <div
              className="mt-4 rounded-lg border border-accent/40 bg-accent/[0.08] p-3 flex items-start gap-3 animate-in fade-in-0 slide-in-from-top-2 duration-300"
              data-testid="pwa-welcome-ios-hint"
            >
              <ArrowDownCircle className="w-5 h-5 text-accent shrink-0 mt-0.5 animate-bounce" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">{c.iosBounceTitle}</p>
                <p className="text-[11px] text-foreground/70 leading-snug mt-0.5">{c.iosBounceBody}</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              onClick={onAllowEverything}
              disabled={busy}
              className="h-12 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors text-[13px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2"
              data-testid="pwa-welcome-allow"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {c.working}
                </>
              ) : (
                <>
                  <BellRing className="w-4 h-4" />
                  {c.allow}
                </>
              )}
            </button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onNever}
                disabled={busy}
                className="text-[11px] uppercase tracking-[0.14em] text-foreground/50 hover:text-foreground/80 transition-colors"
                data-testid="pwa-welcome-never"
              >
                {c.never}
              </button>
              <button
                type="button"
                onClick={onLater}
                disabled={busy}
                className="text-[11px] uppercase tracking-[0.14em] text-foreground/70 hover:text-foreground transition-colors"
                data-testid="pwa-welcome-later"
              >
                {c.later}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PwaWelcomeModal;
